import React from 'react';
import { IProps, IFormValues } from './types';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { Input } from 'formik-antd';
import * as Yup from 'yup';
import Captcha from 'components/Captcha';
import { Link, withRouter } from 'react-router-dom';
import { MemberLoginReq } from 'bff/models/login';
import service from 'bff/services/loginService';
import sweetAlertService from 'utils/sweetAlertService';
import { CodeId } from 'constants/enums/codeId.enum';
import { SweetAlertIcon } from 'constants/enums/sweetAlertIcon.enum';
import commonService from 'utils/commonService';
import { RouterSettings } from 'constants/models/routerSettings';
import InputWithRegex from 'components/InputWithRegex';
import { useDispatch } from 'react-redux';
import { setLoginModalVisible } from 'redux/actions/modal.action';

/**
 * @description 表單驗證
 */
const validationSchema = Yup.object().shape({
  MEMBER_ID: Yup.string().required('此為必填欄位。')
    .length(10, '輸入帳號長度不可超過（少於）10 字元')
    .test('removeSpace', '您所輸入帳號錯誤，請再次確認', val => val?.replace(' ', '').length === 10),
  CAPTCHA_CODE: Yup.string().required('此為必填欄位。')
    .length(4, '驗證碼長度不可超過（少於）4 字元。')
    .test('removeSpace', '您所輸入圖形驗證碼錯誤，請再次確認', val => val?.replace(' ', '').length === 4)
});

/**
 * @description 表單初始值設定
*/
const initialVal: IFormValues = {
  MEMBER_ID: '',
  CAPTCHA_CODE: ''
};

/** 會員登入 */
const MemberLogin: React.FC<IProps> = (props) => {
  const reduxDispatch = useDispatch();

  /**
   * @description 更新驗證碼
   */
  const fetchCaptcha = (setFieldValue: any) => {
    setFieldValue('CAPTCHA_CODE', '');
    props.onCaptchaChange();
  };

  /**
   * @description 表單送出事件
   */
  const handleFormSubmit = async (values: IFormValues) => {
    const memberLoginReq: MemberLoginReq = {
      MEMBER_ID: values.MEMBER_ID,
      CAPTCHA_SN: props.captcha.SN,
      CAPTCHA_CODE: window.btoa(values.CAPTCHA_CODE)
    };

    const result = await service.memberLogin(memberLoginReq);

    if (typeof result === 'string') {
      sweetAlertService.modal({
        icon: SweetAlertIcon.Info,
        html: result
      });
    } else {
      // console.log({ result });
      props.onSubmit(values.MEMBER_ID, { ...result.TRANRS });
    }
  };

  /**
   * @description 點選註冊事件
   * @param event event object
   */
  const handleRegisterClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    // 點擊「註冊」後，關閉登入視窗，避免返回投保流程頁後直接被開啟而觸發不可預期的流程 (2025/02/10)
    reduxDispatch(setLoginModalVisible(false));
    handleRedirect(event, RouterSettings.Signup);
  };

  /**
   * @description 跳轉至註冊頁
  */
  const handleRedirect = (e: React.MouseEvent<HTMLAnchorElement>, pathName: string) => {
    e.preventDefault();
    const path = {
      pathname: pathName,
      state: { originUrl: props.originUrl }
    };

    props.history.push(path);
  };

  // #region 產生驗證碼圖片

  let captcha: any = null;
  if (props.captcha && props.captcha.CAPTCHA_CODE && props.captcha.CAPTCHA_CODE.length > 0) {
    // 取得、解譯驗證碼
    const CAPTCHA_CODE = window.atob(props.captcha.CAPTCHA_CODE);
    // console.log('CAPTCHA_CODE', CAPTCHA_CODE);
    captcha = (
      <Captcha
        captchaText={CAPTCHA_CODE}
        curveLineCount={5}
        dotCount={30}
        width={100}
        height={40}
      />
    );
  }

  // #endregion

  return (
    <div className="member-login auth-block">
      <Formik
        initialValues={initialVal}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
        enableReinitialize
      >
        {({ errors, handleChange, setFieldValue, setFieldTouched, touched }) => (
          <Form>
            <div className="auth-block__row">
              <h3 className="auth-block__title">
                {props.title
                  ? <span>{commonService.htmlParser(props.title)}</span>
                  : commonService.getCodeMsg(props.UICode, CodeId.U010)}
              </h3>
              <div className={'element__form-group' + (errors.MEMBER_ID && touched.MEMBER_ID ? ' element__form-group--invalid' : '')}>
                <Input.Password
                  name="MEMBER_ID"
                  id="MEMBER_ID"
                  className="element__antd-input-password"
                  autoComplete="off"
                  placeholder="請輸入帳號 (身份證字號/居留證號)"
                  maxLength={10}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    handleChange(e);
                    const val = e.currentTarget.value ? e.currentTarget.value.toUpperCase() : e.currentTarget.value;
                    setFieldValue('MEMBER_ID', val);
                  }}
                />
                <div className="element__error-message">
                  <ErrorMessage name="MEMBER_ID" />
                </div>
              </div>
              <div className={'element__form-group' + (errors.CAPTCHA_CODE && touched.CAPTCHA_CODE ? ' element__form-group--invalid' : '')}>
                <div className="auth-block__captcha">
                  <Field
                    component={InputWithRegex}
                    name="CAPTCHA_CODE"
                    id="captcha-code"
                    className="element__input"
                    autoComplete="off"
                    placeholder="請輸入圖形驗證碼"
                    maxLength={4}
                    onBlur={() => {
                      setFieldTouched('CAPTCHA_CODE', true);
                    }}
                  />
                  <div className="auth-block__captcha-code">
                    {captcha}
                  </div>
                  <button type="button" className="auth-block__refresh-btn" onClick={() => fetchCaptcha(setFieldValue)} />
                </div>
                <div className="element__error-message">
                  <ErrorMessage name="CAPTCHA_CODE" />
                </div>
              </div>
              <button type="submit" className="element__button element__button--red">登入</button>
              <Link
                to={RouterSettings.Signup}
                className="element__button element__button--black"
                onClick={handleRegisterClick}
              >
                註冊
              </Link>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default withRouter(MemberLogin);
